import { borderRadius, boxShadows, palette, spaces } from '@kimuno/theme';
import { Box, IconButton, Popper, type PopperProps, SvgIcon, Typography } from '@mui/material';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import getMuiIcon from '../../utils/getMuiIcon';

const ARROW_SIZE = 12;
// Below the smallest theme radius; 4px already blunts a 12px arrow. A unit
// string, because `sx` multiplies a bare number by the theme's shape unit.
const ARROW_TIP_RADIUS = '2px';
const DEFAULT_MAX_WIDTH = 340;

export type AmbientHintPlacement = Extract<PopperProps['placement'], 'bottom' | 'bottom-end'>;

interface AmbientHintProps {
	anchorEl: HTMLElement | null;
	open: boolean;
	placement: AmbientHintPlacement;
	title?: string;
	body: string;
	maxWidth?: number;
	onClose: () => void;
}

const getBubbleStyles = (maxWidth: number) => ({
	position: 'relative',
	maxWidth,
	padding: spaces.md,
	paddingRight: spaces.xxl,
	borderRadius: borderRadius.sm,
	backgroundColor: palette.primary.main,
	color: palette.primary.contrastText,
	boxShadow: boxShadows.elevation3,
});

// Popper positions the wrapper over the anchor's centre with an inline
// transform, so the rotation has to live on a pseudo-element.
const arrowStyles = {
	position: 'absolute',
	top: -ARROW_SIZE / 2,
	width: ARROW_SIZE,
	height: ARROW_SIZE,
	'&::before': {
		content: '""',
		display: 'block',
		width: '100%',
		height: '100%',
		borderRadius: ARROW_TIP_RADIUS,
		backgroundColor: palette.primary.main,
		transform: 'rotate(45deg)',
	},
};

const closeButtonStyles = {
	position: 'absolute',
	top: spaces.xs,
	right: spaces.xs,
	color: 'inherit',
};

/**
 * The ambient tier of the proactive-campaign rules: an anchored, non-blocking
 * bubble with an arrow and an X, no call to action. Below the anchor only.
 */
const AmbientHint = ({
	anchorEl,
	open,
	placement,
	title,
	body,
	maxWidth = DEFAULT_MAX_WIDTH,
	onClose,
}: AmbientHintProps) => {
	const { t } = useTranslation();
	const [arrowEl, setArrowEl] = useState<HTMLElement | null>(null);
	const labelId = useId();

	return (
		<Popper
			open={open && anchorEl !== null}
			anchorEl={anchorEl}
			placement={placement}
			modifiers={[
				{ name: 'offset', options: { offset: [0, ARROW_SIZE] } },
				{ name: 'arrow', options: { element: arrowEl, padding: ARROW_SIZE } },
			]}
			// Above the page chrome, below modal dialogs.
			sx={{ zIndex: (theme) => theme.zIndex.modal - 1 }}
		>
			<Box role="dialog" aria-labelledby={labelId} sx={getBubbleStyles(maxWidth)}>
				<Box ref={setArrowEl} sx={arrowStyles} />
				{title && (
					<Typography id={labelId} variant="h6" component="p" color="inherit">
						{title}
					</Typography>
				)}
				<Typography
					id={title ? undefined : labelId}
					variant="body2"
					color="inherit"
					sx={title ? { marginTop: spaces.xxs } : undefined}
				>
					{body}
				</Typography>
				<IconButton aria-label={t('Close')} onClick={onClose} size="small" sx={closeButtonStyles}>
					<SvgIcon component={getMuiIcon('close')} fontSize="small" />
				</IconButton>
			</Box>
		</Popper>
	);
};

export default AmbientHint;
